import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../../../components/dashboard-layout";
import { FileUploadArea } from "../../../components/file-upload-area";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../../contexts/theme-provider";
import { useAuth } from "../../../contexts/auth-context";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { cn } from "../../../lib/utils";
import { SearchableSelect } from "../../../components/searchable-select";

import {
  TrendingUp,
  FileText,
  FileSpreadsheet,
  Brain,
  BarChart3,
  Cpu,
  Download,
  Building2,
  Globe,
  CalendarIcon,
  ChevronRight,
  CheckCircle2,
  Layers,
  Banknote,
} from "lucide-react";
import {
  handleProcessReportFile,
  checkCachedCommentary,
} from "../../../services/reports";
import type { CachedCommentaryPreview } from "../../../services/reports";
import {
  handleGetInstitutionSectors,
  handleGetInstitutionSubSectors,
} from "../../../services/dashboard";
import {
  getAllCountries,
  getAllCurrenciesByCountry,
  getAllCurrencyConvRate,
} from "../../../services/lookup";
import { BulkFileUpload } from "../../../components/BulkFileUpload";
import { useNavigate } from "react-router-dom";
import { BulkProcessingOverlay } from "../../../components/BulkProcessingOverlay";
import { getAllOrgBySector } from "../../../services/organizations";

interface DashboardPageProps {
  onFileReady: (file: any, sector: any, subSector: any) => void;
  onReportCreated: (reportData: any) => void;
}

interface InstitutionSector {
  id: number;
  name: string;
}

interface InstitutionSubSector {
  id: number;
  institution_sector_id: number;
  name: string;
}

function StepIndicator({
  step,
  currentStep,
  label,
  theme,
}: {
  step: number;
  currentStep: number;
  label: string;
  theme: string;
}) {
  const done = currentStep > step;
  const active = currentStep === step;
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300",
          done
            ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
            : active
              ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg ring-4 ring-blue-500/30"
              : theme === "dark"
                ? "bg-gray-700 text-gray-400"
                : "bg-gray-200 dark:bg-slate-800 text-gray-400",
        )}
      >
        {done ? <CheckCircle2 className="w-4 h-4" /> : step}
      </div>
      <span
        className={cn(
          "text-sm font-semibold hidden sm:block transition-all duration-300",
          active
            ? theme === "dark"
              ? "text-white"
              : "text-gray-900 dark:text-white"
            : theme === "dark"
              ? "text-gray-500"
              : "text-gray-400",
        )}
      >
        {label}
      </span>
    </div>
  );
}

function UploadModeTab({
  isBulkUpload,
  setIsBulkUpload,
  theme,
}: {
  isBulkUpload: boolean;
  setIsBulkUpload: (v: boolean) => void;
  theme: string;
}) {
  return (
    <div
      className={cn(
        "inline-flex rounded-2xl p-1.5 gap-1 border-2 shadow-inner",
        theme === "dark"
          ? "bg-gray-800/80 border-gray-700"
          : "bg-gray-100/80 dark:bg-slate-900/80 border-gray-200 dark:border-slate-700",
      )}
    >
      {[
        { value: false, icon: FileSpreadsheet, label: "Single" },
        { value: true, icon: Layers, label: "Bulk" },
      ].map(({ value, icon: Icon, label }) => {
        const active = isBulkUpload === value;
        return (
          <button
            key={label}
            onClick={() => setIsBulkUpload(value)}
            className={cn(
              "relative cursor-pointer flex items-center gap-2.5 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300",
              active
                ? value
                  ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-[1.02]"
                  : "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg scale-[1.02]"
                : theme === "dark"
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50"
                  : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-100 hover:bg-white/70 dark:hover:bg-slate-900/70",
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function DashboardPage({ onReportCreated }: DashboardPageProps) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [mounted, setMounted] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sector, setSector] = useState<any>();
  const [subSector, setSubSector] = useState<any>();
  const [convertResIn, setConvertResIn] = useState<any>("million");
  const [reportName, setReportName] = useState("");
  const [orgId, setOrgId] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("");
  const [selectedRate, setSelectedRate] = useState("");
  const [otherInfo, setOtherInfo] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingStage, setProcessingStage] = useState(0);
  const [fileValidationStatus, setFileValidationStatus] = useState<
    "none" | "valid" | "invalid"
  >("none");
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [isBulkUpload, setIsBulkUpload] = useState(false);

  // New state for dynamic sectors and sub-sectors
  const [sectors, setSectors] = useState<InstitutionSector[]>([]);
  const [subSectors, setSubSectors] = useState<InstitutionSubSector[]>([]);
  const [isLoadingSectors, setIsLoadingSectors] = useState(false);
  const [isLoadingSubSectors, setIsLoadingSubSectors] = useState(false);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [sectorsError, setSectorsError] = useState<string | null>(null);
  const [subSectorsError, setSubSectorsError] = useState<string | null>(null);

  // Countries & orgs
  const [countries, setCountries] = useState<any>([]);
  const [currencies, setCurrencies] = useState<any>([]);
  const [orgs, setOrgs] = useState<any>([]);
  const [currencyRates, setCurrencyRates] = useState<any>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [currenciesError, setCurrenciesError] = useState<any>(null);
  const [ratesError, setRatesError] = useState<any>(null);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  const [pdfData, setPdfData] = useState<any>(null);
  const [auditorOpinion, setAuditorOpinion] = useState<any>(null);
  const [fileId, setFileId] = useState<any>(null)

  // Saved (admin pre-generated) commentary preview for the selected org
  const [cachedInfo, setCachedInfo] = useState<CachedCommentaryPreview | null>(null);
  const [isCheckingCached, setIsCheckingCached] = useState(false);
  // Whether the user chose to reuse the saved version (true) or upload their own file (false)
  const [useSavedVersion, setUseSavedVersion] = useState(true);

  // Bulk progress state
  const [bulkProgress, setBulkProgress] = useState<{
    total: number;
    completed: number;
    failed: number;
  } | null>(null);

  const uploadSectionRef = useRef<HTMLDivElement>(null);
  const configurationSectionRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const cachedKeyRef = useRef("");
  const showTemplateDropdownRef = useRef(showTemplateDropdown);

  const processingStages = [
    "Uploading file to secure server...",
    "Analyzing data structure...",
    "Running AI algorithms...",
    "Generating insights...",
    "Finalizing report...",
  ];

  const currentStep = sector && subSector ? 2 : 1;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch sub-sectors when sector changes
  useEffect(() => {
    if (sector && user?.access_token) {
      fetchSubSectors(parseInt(sector));
    } else {
      setSubSectors([]);
    }
  }, [sector, user?.access_token]);

  useEffect(() => {
    if (user?.access_token) {
      fetchCountries();
      fetchSectors();
    }
  }, [user?.access_token]);

  // Refetch countries when sub-sector changes (filter by sector + sub-sector)
  useEffect(() => {
    if (user?.access_token && sector && subSector) {
      fetchCountries(parseInt(sector), parseInt(subSector));
    }
  }, [sector, subSector, user?.access_token]);

  useEffect(() => {
    if (country && sector && subSector && user?.access_token)
      fetchOrgBySector();
    else setOrgs([]);

    if (country) fetchCurrencies();
  }, [country, user?.access_token]);

  // Check for a saved (pre-generated) commentary once org + sector + sub-sector
  // are chosen. If one exists we show its stored financial table and skip upload.
  useEffect(() => {
    if (!(orgId && sector && subSector && user?.access_token)) {
      setCachedInfo(null);
      cachedKeyRef.current = "";
      return;
    }
    // Only treat it as a fresh selection when the org/sector/sub-sector changes —
    // a currency change should just re-render the preview in the new currency
    // without wiping the user's saved-vs-upload choice or uploaded file.
    const key = `${orgId}|${sector}|${subSector}`;
    const isNewSelection = cachedKeyRef.current !== key;
    cachedKeyRef.current = key;
    if (isNewSelection) setIsCheckingCached(true);
    checkCachedCommentary(orgId, sector, subSector, user.access_token, currency || undefined)
      .then((res) => {
        setCachedInfo(res && res.exists ? res : null);
        if (res && res.exists && isNewSelection) {
          // Default to using the saved version — no file needed unless the
          // user opts to upload their own.
          setUseSavedVersion(true);
          setSelectedFile(null);
          setFileValidationStatus("none");
        }
      })
      .catch(() => setCachedInfo(null))
      .finally(() => {
        if (isNewSelection) setIsCheckingCached(false);
      });
  }, [orgId, sector, subSector, currency, user?.access_token]);

  useEffect(() => {
    // Always fetch conversion rates when currency is selected
    if (currency && country && user?.access_token) {
      fetchConvRateByCurrency();
    } else {
      setCurrencyRates([]);
      setSelectedRate("");
    }
  }, [currency, country, user?.access_token]);

  // Reset bulk mode when NBFI is selected
  useEffect(() => {
    const selectedSector = sectors.find((s) => s.id === parseInt(sector));
    if (selectedSector?.name === "NBFI") {
      setIsBulkUpload(false);
    }
  }, [sector, sectors]);

  const fetchSectors = async () => {
    if (!user?.access_token) return;
    setIsLoadingSectors(true);
    setSectorsError(null);
    try {
      const data = await handleGetInstitutionSectors(user.access_token);
      setSectors(data);
    } catch (err: any) {
      setSectorsError(err.message || "Failed to load sectors");
    } finally {
      setIsLoadingSectors(false);
    }
  };

  const fetchSubSectors = async (sectorId: number) => {
    if (!user?.access_token) return;
    setIsLoadingSubSectors(true);
    setSubSectorsError(null);
    try {
      const data = await handleGetInstitutionSubSectors(
        sectorId,
        user.access_token,
      );
      setSubSectors(data);
    } catch (err: any) {
      setSubSectorsError(err.message || "Failed to load sub-sectors");
    } finally {
      setIsLoadingSubSectors(false);
    }
  };

  const fetchCountries = async (sectorId?: number, subSectorId?: number) => {
    if (!user?.access_token) return;
    setIsLoadingCountries(true);
    setCountriesError(null);
    try {
      const data = await getAllCountries(user.access_token, sectorId, subSectorId);
      setCountries(data);
    } catch (err: any) {
      setCountriesError(err.message || "Failed to load countries");
    } finally {
      setIsLoadingCountries(false);
    }
  };

  const fetchCurrencies = async () => {
    if (!user?.access_token) return;
    setIsLoadingCurrencies(true);
    setCurrenciesError(null);
    try {
      const data = await getAllCurrenciesByCountry(user.access_token, country);
      setCurrencies(data);
    } catch (err: any) {
      setCurrenciesError(err.message || "Failed to load countries");
    } finally {
      setIsLoadingCurrencies(false);
      console.log(currenciesError);
    }
  };

  const fetchOrgBySector = async () => {
    if (!user?.access_token) return;
    setIsLoadingOrgs(true);
    setOrgsError(null);
    try {
      const res = await getAllOrgBySector(
        user.access_token,
        country,
        sector,
        subSector,
        true, // Get all organizations for home report generation
      );
      setOrgs(res);
    } catch (err: any) {
      setOrgsError(err.message || "Failed to load orgs");
      setOrgs([]);
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const fetchConvRateByCurrency = async () => {
    if (!user?.access_token) return;
    setIsLoadingRates(true);
    setRatesError(null);
    try {
      console.log(currency);
      const res = await getAllCurrencyConvRate(
        user.access_token,
        country,
        currency,
      );
      setCurrencyRates(res);
      // Auto-select the latest rate (first in the list, assumed sorted by date desc)
      if (res && res.length > 0) {
        setSelectedRate(String(res[0].id));
      }
    } catch (err: any) {
      setCurrencyRates([]);
      setSelectedRate("");
      setRatesError(err.message || "Failed to load orgs");
    } finally {
      setIsLoadingRates(false);
    }
    console.log(isLoadingRates, ratesError);
  };

  useEffect(() => {
    // Reset dependent selections
    setSubSector("");
    setOrgId("");
    setCountry("");
    setCurrency("");
    setSelectedRate("");

    // Reset file + report config
    setSelectedFile(null);
    setReportName("");
    setOtherInfo("");

    // Reset lists
    setOrgs([]);
    setCurrencies([]);
    setCurrencyRates([]);

    // Reset UI states
    setFileValidationStatus("none");
    setError(null);

    // Optional: reset bulk mode
    setIsBulkUpload(false);
  }, [sector]);

  useEffect(() => {
    if (selectedFile && configurationSectionRef.current) {
      setTimeout(() => {
        configurationSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 300);
    }
  }, [selectedFile]);

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout>;
    if (isProcessing && !isBulkUpload) {
      interval = setInterval(() => {
        setProcessingStage((prev) => {
          if (prev >= processingStages.length - 1) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, isBulkUpload]);

  useEffect(() => {
    showTemplateDropdownRef.current = showTemplateDropdown;
  }, [showTemplateDropdown]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (showTemplateDropdownRef.current) setShowTemplateDropdown(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  if (!mounted) return null;

  const handleFileReady = (file: File, isValid: boolean) => {
    if (isValid) {
      setError(null);
      setSelectedFile(file);
      setFileValidationStatus("valid");
    } else {
      setSelectedFile(null);
      setFileValidationStatus("invalid");
    }
  };

  const processSingleFile = async (fileData: {
    file: File;
    reportName: string;
    orgId: string;
    country: string;
    currency: string;
    selectedRate: string;
    convertResIn: string;
  }) => {
    const {
      file,
      reportName,
      orgId,
      country,
      currency,
      selectedRate,
      convertResIn,
    } = fileData;

    const fileType = file.type;
    const isExcel =
      fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.toLowerCase().endsWith(".xlsx");

    const isPDF =
      fileType === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isExcel && !isPDF) {
      throw new Error("Invalid file type");
    }

    const selectedSectorObj = sectors.find((s) => s.id === parseInt(sector));
    const selectedSubSectorObj = subSectors.find(
      (s) => s.id === parseInt(subSector),
    );

    // Excel validation — FI bank-type subsectors share the same template
    if (
      isExcel &&
      selectedSectorObj?.name === "FI" &&
      (selectedSubSectorObj?.name === "Commercial Banks" ||
        selectedSubSectorObj?.name === "Commercial Orgs" ||
        selectedSubSectorObj?.name === "Development Financial Institutions")
    ) {
      const requiredSheets = [
        "Key Financials",
        "Balance Sheet",
        "Income Statement",
        "Ratios",
        "Off Balance Sheet",
      ];

      const arrayBuffer = await file.arrayBuffer();
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      const missingSheets = requiredSheets.filter(
        (s) => !workbook.SheetNames.includes(s),
      );

      if (missingSheets.length > 0) {
        throw new Error(`Missing sheets: ${missingSheets.join(", ")}`);
      }
    }

    let finalName = reportName.trim();
    if (isExcel && !finalName.endsWith(".xlsx")) finalName += ".xlsx";
    if (isPDF && !finalName.endsWith(".pdf")) finalName += ".pdf";

    const renamedFile = new File([file], finalName, {
      type: file.type,
    });

    const formData = new FormData();
    formData.append("file", renamedFile);
    formData.append("user_id", user!.id);
    formData.append("name", reportName);
    formData.append("report_type", selectedSectorObj?.name || sector);
    formData.append("sector_id", sector);
    formData.append("report_type_2", selectedSubSectorObj?.name || subSector);
    formData.append("sub_sector_id", subSector);
    formData.append("organization_id", orgId);
    formData.append("country_code", country);
    formData.append("currency", currency);
    formData.append("conversion_rate_id", selectedRate);
    formData.append("convert_in", convertResIn);

    if (fileId) {
      formData.append("pdf_file_id", fileId);
    }

    if (pdfData) {
      formData.append("pdf_data",  JSON.stringify(pdfData));
    }

    if (auditorOpinion) {
      formData.append("auditor_opinion", JSON.stringify(auditorOpinion));
    }

    if (otherInfo) formData.append("other_info", otherInfo);

    const apiResponse = await handleProcessReportFile(
      formData,
      user!.id,
      abortControllerRef.current!.signal,
      user!.access_token!,
    );

    return {
      ...apiResponse,
      reportName,
      orgId,
      country,
      originalFileName: file.name,
      sentFileName: renamedFile.name,
    };
  };

  // Submit a report that reuses the saved (pre-generated) commentary — no file.
  const processCachedReport = async () => {
    const selectedSectorObj = sectors.find((s) => s.id === parseInt(sector));
    const selectedSubSectorObj = subSectors.find(
      (s) => s.id === parseInt(subSector),
    );

    const formData = new FormData();
    formData.append("use_cached", "true");
    formData.append("user_id", user!.id);
    formData.append("name", reportName);
    formData.append("report_type", selectedSectorObj?.name || sector);
    formData.append("sector_id", sector);
    formData.append("report_type_2", selectedSubSectorObj?.name || subSector);
    formData.append("sub_sector_id", subSector);
    formData.append("organization_id", orgId);
    formData.append("country_code", country);
    formData.append("currency", currency);
    formData.append("conversion_rate_id", selectedRate);
    formData.append("convert_in", convertResIn);
    if (otherInfo) formData.append("other_info", otherInfo);

    const apiResponse = await handleProcessReportFile(
      formData,
      user!.id,
      abortControllerRef.current!.signal,
      user!.access_token!,
    );

    return {
      ...apiResponse,
      reportName,
      orgId,
      country,
      originalFileName: cachedInfo
        ? `Saved commentary (FY ${cachedInfo.financial_year})`
        : "Saved commentary",
      sentFileName: "",
    };
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (!user?.id) {
      setError("You must be logged in to create a report.");
      return;
    }
    if (!reportName) {
      setError("Please provide a name for the report.");
      return;
    }
    if (!orgId) {
      setError("Please provide the org name.");
      return;
    }
    if (!country) {
      setError("Please provide the country.");
      return;
    }
    // Use the saved commentary only if it exists AND the user chose it.
    const useCached = !!(cachedInfo && useSavedVersion);

    if (!useCached && !selectedFile) {
      setError("Please upload a file first.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProcessingStage(0);
    abortControllerRef.current = new AbortController();

    try {
      const apiResponse = useCached
        ? await processCachedReport()
        : await processSingleFile({
            file: selectedFile!,
            reportName,
            orgId,
            country,
            currency,
            selectedRate,
            convertResIn,
          });

      const fullReportData = {
        ...apiResponse,
        reportName,
        orgId,
        country,
        currency,
        conversion_rate: selectedRate,
        otherInfo,
        sector,
        subSector,
        originalFileName: selectedFile
          ? selectedFile.name
          : `Saved commentary (FY ${cachedInfo?.financial_year ?? ""})`,
        createdAt: new Date().toISOString(),
      };

      sessionStorage.setItem("currentReport", JSON.stringify(fullReportData));
      onReportCreated(fullReportData);
    } catch (err: any) {
      const detail = err.response?.data?.detail;

      let message = "";
      if (err.response?.status === 403 && detail === "Quota exceeded") {
        message =
          "Your report generation quota has been exceeded. Please upgrade your plan or contact support.";
      } else {
        message = detail || err.response?.data?.message || err.message || "An unknown error occurred.";
      }

      setError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkSubmit = async (filesData: any[]) => {
    if (!user?.id) {
      setError("You must be logged in to create reports.");
      return;
    }

    setBulkProgress({ total: filesData.length, completed: 0, failed: 0 });
    setIsProcessing(true);
    setError(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    let completed = 0;
    let failed = 0;
    const results: any[] = [];

    try {
      for (const fileItem of filesData) {
        try {
          const res = await processSingleFile({
            file: fileItem.file,
            reportName: fileItem.reportName,
            orgId: fileItem.organization || orgId,
            country: fileItem.country || country,
            currency: fileItem.currency || currency,
            selectedRate: fileItem.selectedRate || selectedRate,
            convertResIn: fileItem.convertResIn || convertResIn,
          });

          results.push(res);
          completed++;
        } catch (err) {
          console.error("File failed:", err);
          failed++;
        }

        setBulkProgress({
          total: filesData.length,
          completed,
          failed,
        });
      }

      // After loop
      const finalResponse = {
        total_files: filesData.length,
        successful: completed,
        failed,
        results,
      };

      setBulkProgress({
        total: filesData.length,
        completed,
        failed,
      });

      // allow UI to update
      await new Promise((r) => setTimeout(r, 100));

      sessionStorage.setItem(
        "bulkProcessingData",
        JSON.stringify(finalResponse),
      );

      navigate("/user/reports/bulk-processing", {
        state: { bulkData: finalResponse },
      });
    } catch (err: any) {
      setError(err.message || "Bulk processing failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadSpecificTemplate = async (
    templateType: "commercial_org" | "nbfi" | "generic",
  ) => {
    const fileNameMap = {
      commercial_org: "Commercial_Org_Template.xlsx",
      nbfi: "NBFI_Template.xlsx",
      generic: "Commercial_Org_Template.xlsx",
    };
    
    const fileName = fileNameMap[templateType] ?? "Generic_Template.xlsx";
    const response = await fetch(`/templates/${fileName}`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const floatingOrbVariants = {
    animate: {
      y: [0, -20, 0],
      transition: { duration: 3, repeat: Number.POSITIVE_INFINITY },
    },
  };

  const pulseVariants = {
    animate: {
      scale: [1, 1.1, 1],
      opacity: [0.7, 1, 0.7],
      transition: { duration: 2, repeat: Number.POSITIVE_INFINITY },
    },
  };

  const selectCls = cn(
    "w-full p-4 rounded-xl border-2 text-base font-medium transition-all shadow-md focus:ring-2 focus:ring-blue-500/50",
    theme === "dark"
      ? "bg-gray-700/70 border-gray-600 text-white placeholder-gray-400 dark:placeholder-slate-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      : "bg-white border-gray-300 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-slate-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed",
  );

  // Find local currency from currencies data (the one matching the country's iso2)
  const localCurrency = currencies.find(
    (c: { iso2: string }) => c.iso2 === country
  )?.currency;

  // Get the actual numeric rate value from the selected rate ID
  const selectedRateValue = currencyRates.find(
    (c: { id: number; rate: string }) => c.id === parseInt(selectedRate)
  )?.rate;
  const conversionRateValue = selectedRateValue ? parseFloat(selectedRateValue) : undefined;

  // Saved-version panel: notice + stored financial table + the choice of whether
  // to reuse it. Rendered right after the organization is selected.
  const renderSavedVersionPanel = () => {
    if (!cachedInfo) return null;

    const optionCls = (active: boolean) =>
      cn(
        "flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all w-full",
        active
          ? theme === "dark"
            ? "border-emerald-500 bg-emerald-500/10"
            : "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30"
          : theme === "dark"
            ? "border-gray-700 bg-gray-800/40 hover:border-gray-600"
            : "border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-600",
      );
    const radioCls = (active: boolean) =>
      cn(
        "h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center",
        active
          ? "border-emerald-500"
          : theme === "dark"
            ? "border-gray-500"
            : "border-gray-400 dark:border-slate-500",
      );

    return (
      <Card
        className={cn(
          "!border-2 !shadow-2xl !backdrop-blur-sm",
          theme === "dark"
            ? "!bg-slate-800/50 !border-emerald-700/50"
            : "!bg-white/70 dark:!bg-slate-900/70 !border-emerald-300 dark:!border-emerald-700",
        )}
      >
        <CardContent className="!p-8 !space-y-6">
          <div className="flex items-start gap-3">
            <CheckCircle2
              className={cn(
                "w-6 h-6 mt-0.5 shrink-0",
                theme === "dark" ? "text-emerald-400" : "text-emerald-600 dark:text-emerald-400",
              )}
            />
            <div>
              <h3
                className={cn(
                  "text-lg font-bold",
                  theme === "dark" ? "text-white" : "text-gray-900 dark:text-white",
                )}
              >
                Saved report available
              </h3>
              <p
                className={cn(
                  "text-sm mt-1",
                  theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400",
                )}
              >
                We already have a saved version for this organization for
                financial year{" "}
                <span className="font-semibold">
                  {cachedInfo.financial_year}
                </span>
                {cachedInfo.currency ? ` (${cachedInfo.currency})` : ""}. The
                stored figures are shown below — you can use this saved version
                or upload your own file instead.
              </p>
            </div>
          </div>

          {cachedInfo.rows && cachedInfo.rows.length > 0 && (
            <div
              className={cn(
                "overflow-x-auto rounded-lg border",
                theme === "dark" ? "border-gray-700" : "border-gray-300 dark:border-slate-600",
              )}
            >
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th
                      className={cn(
                        "px-3 py-2 border text-left font-semibold",
                        theme === "dark"
                          ? "bg-gray-800 border-gray-700 text-gray-100"
                          : "bg-gray-100 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white",
                      )}
                    >
                      {cachedInfo.currency || "Metric"}
                    </th>
                    {cachedInfo.columns?.map((c) => (
                      <th
                        key={c}
                        className={cn(
                          "px-3 py-2 border text-left font-semibold",
                          theme === "dark"
                            ? "bg-gray-800 border-gray-700 text-gray-100"
                            : "bg-gray-100 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white",
                        )}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cachedInfo.rows.map((row, i) => (
                    <tr
                      key={row.key}
                      className={
                        i % 2 === 0
                          ? theme === "dark"
                            ? "bg-gray-800/40"
                            : "bg-gray-50 dark:bg-slate-900"
                          : ""
                      }
                    >
                      <td
                        className={cn(
                          "px-3 py-1.5 border font-medium",
                          theme === "dark"
                            ? "border-gray-700 text-gray-200"
                            : "border-gray-200 dark:border-slate-700 text-gray-800 dark:text-slate-100",
                        )}
                      >
                        {row.label}
                      </td>
                      {row.values.map((v, j) => (
                        <td
                          key={j}
                          className={cn(
                            "px-3 py-1.5 border",
                            theme === "dark"
                              ? "border-gray-700 text-gray-300"
                              : "border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200",
                          )}
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Choice: reuse the saved version or upload a new file */}
          <div className="space-y-3">
            <Label
              className={cn(theme === "dark" ? "text-gray-200" : "text-gray-800 dark:text-slate-100")}
            >
              How would you like to proceed?
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setUseSavedVersion(true)}
                className={optionCls(useSavedVersion)}
              >
                <span className={radioCls(useSavedVersion)}>
                  {useSavedVersion && (
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  )}
                </span>
                <span>
                  <span
                    className={cn(
                      "block font-semibold",
                      theme === "dark" ? "text-white" : "text-gray-900 dark:text-white",
                    )}
                  >
                    Use saved version
                  </span>
                  <span
                    className={cn(
                      "block text-xs mt-0.5",
                      theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400",
                    )}
                  >
                    FY {cachedInfo.financial_year} — no upload needed
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setUseSavedVersion(false)}
                className={optionCls(!useSavedVersion)}
              >
                <span className={radioCls(!useSavedVersion)}>
                  {!useSavedVersion && (
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  )}
                </span>
                <span>
                  <span
                    className={cn(
                      "block font-semibold",
                      theme === "dark" ? "text-white" : "text-gray-900 dark:text-white",
                    )}
                  >
                    Upload my own file
                  </span>
                  <span
                    className={cn(
                      "block text-xs mt-0.5",
                      theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400",
                    )}
                  >
                    Generate from a new Excel/PDF
                  </span>
                </span>
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Renders the report input: the saved-version generate action or file upload.
  const renderReportInput = () => {
    const fileUpload = (
      <FileUploadArea
        onFileReady={handleFileReady}
        sector={sectors.find((s) => s.id === parseInt(sector))?.name || sector}
        subSector={
          subSectors.find((s) => s.id === parseInt(subSector))?.name || subSector
        }
        sectorId={sector}
        subSectorId={subSector}
        onStartAnalysis={handleSubmit}
        reportName={reportName}
        setReportName={setReportName}
        otherInfo={otherInfo}
        setOtherInfo={setOtherInfo}
        isProcessing={isProcessing}
        error={error}
        fileValidationStatus={fileValidationStatus}
        setFileId={setFileId}
        setPdfData={setPdfData}
        setAuditorOpinion={setAuditorOpinion}
        localCurrency={localCurrency}
        conversionRate={conversionRateValue}
        reportCurrency={currency}
        countryCode={country}
      />
    );

    if (isCheckingCached) {
      return (
        <Card
          className={cn(
            "!border-2 !shadow-2xl !backdrop-blur-sm",
            theme === "dark"
              ? "!bg-slate-800/50 !border-slate-700"
              : "!bg-white/70 dark:!bg-slate-900/70 !border-slate-300 dark:!border-slate-600",
          )}
        >
          <CardContent className="!p-8">
            <p
              className={cn(
                "text-sm",
                theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400",
              )}
            >
              Checking for a saved version…
            </p>
          </CardContent>
        </Card>
      );
    }

    // Saved version chosen -> just a generate action (name is captured above).
    if (cachedInfo && useSavedVersion) {
      return (
        <Card
          className={cn(
            "!border-2 !shadow-2xl !backdrop-blur-sm",
            theme === "dark"
              ? "!bg-slate-800/50 !border-slate-700"
              : "!bg-white/70 dark:!bg-slate-900/70 !border-slate-300 dark:!border-slate-600",
          )}
        >
          <CardContent className="!p-8 !space-y-4">
            <p
              className={cn(
                "text-sm",
                theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400",
              )}
            >
              Using the saved version for financial year{" "}
              <span className="font-semibold">
                {cachedInfo.financial_year}
              </span>
              . Name your report above, then generate.
            </p>

            {error && (
              <p
                className={cn(
                  "text-sm",
                  theme === "dark" ? "text-red-400" : "text-red-600 dark:text-red-400",
                )}
              >
                {error}
              </p>
            )}

            <Button
              onClick={handleSubmit}
              disabled={isProcessing || !reportName}
              className="w-full sm:w-auto"
            >
              {isProcessing
                ? "Generating..."
                : `Generate Report (FY ${cachedInfo.financial_year})`}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return fileUpload;
  };

  // Reusable component for the configuration and requirements cards
  const renderConfigurationAndRequirements = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
      <div
        className="lg:col-span-2 h-full"
        style={{ position: "relative", zIndex: 10 }}
      >
        <motion.div
          whileHover={{ scale: 1.005, y: -2 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "p-8 rounded-3xl backdrop-blur-sm shadow-2xl border-2 h-full",
            theme === "dark"
              ? "bg-gray-800/70 border-gray-700"
              : "bg-white/90 dark:bg-slate-900/90 border-gray-200 dark:border-slate-700",
          )}
          style={{ overflow: "visible" }}
        >
          <div className="flex items-center gap-4 mb-6">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="p-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-xl"
            >
              <FileText className="w-7 h-7 text-white" />
            </motion.div>
            <h3
              className={cn(
                "text-3xl font-bold",
                theme === "dark" ? "text-white" : "text-gray-900 dark:text-white",
              )}
            >
              Report Configuration
            </h3>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label
                className={cn(
                  "text-sm font-semibold",
                  theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                )}
              >
                Report Name <span className="text-red-500 dark:text-red-400">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter a name for your report"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                required
                maxLength={100}
                className={cn(
                  "p-4 text-base font-medium shadow-md",
                  theme === "dark"
                    ? "bg-gray-700/70 border-gray-600 text-white placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                    : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50",
                )}
              />
            </div>

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              style={{ overflow: "visible" }}
            >
              <div className="space-y-2 relative" style={{ zIndex: 50 }}>
                <Label
                  className={cn(
                    "flex items-center gap-2 text-sm font-semibold",
                    theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                  )}
                >
                  <Globe className="w-4 h-4" />
                  Country <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <div className="relative" style={{ zIndex: 50 }}>
                  <SearchableSelect
                    options={countries.map(
                      (c: { iso2: string; name: string }) => ({
                        value: c.iso2,
                        label: c.name,
                      }),
                    )}
                    value={country}
                    onChange={setCountry}
                    placeholder="Type or select a country" // Changed from "Select or type a country"
                    disabled={isLoadingCountries}
                    theme={theme}
                  />
                </div>
                {countriesError && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{countriesError}</p>
                )}
              </div>

              <div className="space-y-2 relative" style={{ zIndex: 49 }}>
                <Label
                  className={cn(
                    "flex items-center gap-2 text-sm font-semibold",
                    theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                  )}
                >
                  <Building2 className="w-4 h-4" />
                  Organization <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <div className="relative" style={{ zIndex: 49 }}>
                  <SearchableSelect
                    options={orgs?.map((org: { id: number; name: string }) => ({
                      value: org.id,
                      label: org.name,
                    })) || []}
                    value={orgId}
                    onChange={setOrgId}
                    placeholder={
                      !country
                        ? "Select a country first"
                        : "Type or select a org"
                    }
                    disabled={!country || isLoadingOrgs}
                    theme={theme}
                  />
                </div>
                {orgsError && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{orgsError}</p>
                )}
              </div>
            </div>

            {/* Saved-version option appears as soon as an org is selected */}
            {renderSavedVersionPanel()}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  className={cn(
                    "flex items-center gap-2 text-sm font-semibold",
                    theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                  )}
                >
                  <Banknote className="w-4 h-4" />
                  Convert to <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <SearchableSelect
                  options={(() => {
                    const seen = new Set();
                    return currencies
                      .filter((c: { currency: string; }) => {
                        if (seen.has(c.currency)) return false;
                        seen.add(c.currency);
                        return true;
                      })
                      .map((c: { currency: string; currency_name: string; currency_symbol: string }) => ({
                        value: c.currency,
                        label: `${c.currency} - ${c.currency_name} - ${c.currency_symbol}`,
                      }));
                  })()}
                  value={currency}
                  onChange={(value) => setCurrency(value)}
                  disabled={isLoadingCurrencies || !country}
                  placeholder={!country ? "Select a country first" : "Type or select a currency"}
                  theme={theme}
                />
              </div>

              <div className="space-y-2">
                <Label
                  className={cn(
                    "flex items-center gap-2 text-sm font-semibold",
                    theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                  )}
                >
                  <CalendarIcon className="w-4 h-4" />
                  Date <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <SearchableSelect
                  options={currencyRates.map(
                    (c: { id: number; conversion_date: string }) => ({
                      value: c.id,
                      label: c.conversion_date,
                    }),
                  )}
                  value={selectedRate}
                  disabled={!currency || isLoadingRates}
                  onChange={(value) => setSelectedRate(value)}
                  placeholder={
                    !currency
                      ? "Select a currency first"
                      : isLoadingRates
                        ? "Loading rates..."
                        : currencyRates.length === 0
                          ? "No rates available"
                          : "Select conversion date"
                  }
                  theme={theme}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* File requirements card */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="h-full"
      >
        <Card
          className={cn(
            "shadow-2xl border-2 h-full flex flex-col justify-between",
            theme === "dark"
              ? "bg-gray-800/70 border-gray-700"
              : "bg-gradient-to-br from-blue-50/80 dark:from-blue-900/30 to-cyan-50/60 dark:to-cyan-900/30 border-blue-100 dark:border-blue-800",
          )}
        >
          <CardContent className="p-6 flex-1 flex flex-col">
            {/* <motion.div
                animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className={cn(
                  "p-3 rounded-2xl shadow-xl",
                  theme === "dark"
                    ? "bg-gradient-to-br from-blue-900/50 to-cyan-900/40"
                    : "bg-gradient-to-br from-blue-100 dark:from-blue-900/30 to-cyan-100 dark:to-cyan-900/30",
                )}
              >
                <FileSpreadsheet
                  className={cn(
                    "h-6 w-6",
                    theme === "dark" ? "text-blue-300" : "text-blue-600 dark:text-blue-400",
                  )}
                />
              </motion.div> */}
            <div className="flex items-start gap-4 flex-1">
              <div className="flex-1 flex flex-col">
                <h4
                  className={cn(
                    "font-bold flex gap-2 text-lg mb-3",
                    theme === "dark" ? "text-blue-100" : "text-blue-900 dark:text-blue-200",
                  )}
                >
                  <span>
                    <FileSpreadsheet
                      className={cn(
                        "h-6 w-6",
                        theme === "dark" ? "text-blue-300" : "text-blue-600 dark:text-blue-400",
                      )}
                    />
                  </span>
                  File Requirements (for Excel uploads)
                </h4>

                <div className="flex-1 overflow-y-auto max-h-64 pr-2 custom-scrollbar">
                  <style>{`
                    .custom-scrollbar::-webkit-scrollbar { width: 3px; }
                    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                      background: ${theme === "dark" ? "rgba(96, 165, 250, 0.2)" : "rgba(59, 130, 246, 0.15)"};
                      border-radius: 10px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                      background: ${theme === "dark" ? "rgba(96, 165, 250, 0.4)" : "rgba(59, 130, 246, 0.3)"};
                    }
                    .custom-scrollbar { scrollbar-width: thin; scrollbar-color: ${theme === "dark" ? "rgba(96, 165, 250, 0.2)" : "rgba(59, 130, 246, 0.15)"} transparent; }
                  `}</style>
                  <ul className="space-y-2">
                    {[
                      "Uploaded data should be in domestic currency of the country",
                      "Uploaded data should be in Millions",
                      "Maximum file size: 10MB",
                      "File must contain financial data for analysis",
                      "Ensure file is not password protected",
                      ...(sector &&
                      ["Commercial Banks", "Commercial Orgs", "Development Financial Institutions"].includes(
                        subSectors.find((s) => s.id === parseInt(subSector))
                          ?.name || "",
                      )
                        ? [
                            "For Banks & DFIs: Must include Key Financials, Balance Sheet, Income Statement, Ratios, and Off Balance Sheet sheets",
                          ]
                        : []),
                    ].map((req, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        whileHover={{ x: 4 }}
                        className={cn(
                          "flex items-start gap-3 p-2 rounded-lg shadow-sm",
                          theme === "dark"
                            ? "bg-slate-700/30 hover:bg-slate-700/50"
                            : "bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-blue-100/20 dark:border-blue-800 hover:bg-white dark:hover:bg-slate-900 transition-all duration-200",
                        )}
                      >
                        <div
                          className={cn(
                            "w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5",
                            theme === "dark"
                              ? "bg-gradient-to-r from-blue-400 to-cyan-400"
                              : "bg-gradient-to-r from-blue-500 to-cyan-500",
                          )}
                        />
                        <span
                          className={cn(
                            "text-sm leading-snug",
                            theme === "dark"
                              ? "text-blue-200"
                              : "text-blue-800 dark:text-blue-200 font-medium",
                          )}
                        >
                          {req}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <div className="relative mt-4">
                  <Button
                    variant="default"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleDownloadSpecificTemplate(sector == 1 ? "commercial_org" : "nbfi");
                      // setShowTemplateDropdown(!showTemplateDropdown);
                    }}
                    className="w-full cursor-pointer py-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 rounded-lg flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </Button>

                  {showTemplateDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={cn(
                        "absolute top-full left-0 right-0 mt-2 rounded-lg shadow-xl border-2 z-20",
                        theme === "dark"
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700",
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {[
                        {
                          key: "commercial_org" as const,
                          label: "Commercial Org Template",
                        },
                        { key: "nbfi" as const, label: "NBFI Template" },
                      ].map(({ key, label }, i, arr) => (
                        <button
                          key={key}
                          onClick={() => {
                            handleDownloadSpecificTemplate(key);
                            setShowTemplateDropdown(false);
                          }}
                          className={cn(
                            "w-full cursor-pointer text-left px-4 py-3 hover:bg-opacity-50 transition-colors",
                            i < arr.length - 1 && "border-b",
                            theme === "dark"
                              ? "hover:bg-gray-700 border-gray-700 text-white"
                              : "hover:bg-blue-50 dark:hover:bg-blue-900/30 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white",
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );

  return (
    <DashboardLayout>
      <div
        className={`flex-1 flex flex-col p-4 md:p-6 lg:p-10 !min-h-screen relative overflow-hidden ${
          theme === "dark"
            ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
            : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
        }`}
      >
        {/* Ambient background orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full blur-3xl ${
                theme === "dark" ? "bg-blue-500/5" : "bg-purple-300/10 dark:bg-purple-800/40"
              }`}
              style={{
                width: Math.random() * 400 + 200,
                height: Math.random() * 400 + 200,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
              animate={{
                x: [0, Math.random() * 100 - 50],
                y: [0, Math.random() * 100 - 50],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: Math.random() * 20 + 15,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>

        {/* Processing overlays */}
        <AnimatePresence>
          {isProcessing &&
            (isBulkUpload && bulkProgress ? (
              <BulkProcessingOverlay
                total={bulkProgress.total}
                completed={bulkProgress.completed}
                failed={bulkProgress.failed}
                isComplete={false}
              />
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md"
              >
                <div
                  className={`absolute inset-0 ${theme === "dark" ? "bg-black/70" : "bg-white/90 dark:bg-slate-900/90"}`}
                />
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", damping: 15 }}
                  className={`relative z-10 w-full max-w-2xl mx-4 p-10 rounded-3xl shadow-2xl border-2 ${
                    theme === "dark"
                      ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700"
                      : "bg-gradient-to-br from-white to-blue-50 border-blue-100"
                  }`}
                >
                  <div className="text-center mb-10">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-2xl"
                    >
                      <Brain className="h-12 w-12 text-white" />
                    </motion.div>
                    <motion.h2
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`text-3xl font-bold mb-4 ${theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"}`}
                    >
                      AI Analysis in Progress
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className={`mb-2 text-lg ${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}
                    >
                      {processingStages[processingStage]}
                    </motion.p>
                    <div className="w-full bg-gray-200 rounded-full h-3 mt-8 dark:bg-gray-700 overflow-hidden shadow-inner">
                      <motion.div
                        initial={{ width: "0%" }}
                        animate={{
                          width: `${((processingStage + 1) / processingStages.length) * 100}%`,
                        }}
                        transition={{ duration: 0.5 }}
                        className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-6 mb-10">
                    {[Cpu, BarChart3, TrendingUp].map((Icon, index) => (
                      <motion.div
                        key={index}
                        variants={floatingOrbVariants}
                        animate="animate"
                        transition={{ delay: index * 0.3 }}
                        className={`p-6 rounded-2xl flex flex-col items-center shadow-lg ${
                          theme === "dark" ? "bg-gray-700/50" : "bg-blue-100/50"
                        }`}
                      >
                        <motion.div
                          variants={pulseVariants}
                          animate="animate"
                          transition={{ delay: index * 0.5 }}
                          className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center mb-3 shadow-xl"
                        >
                          <Icon className="h-7 w-7 text-white" />
                        </motion.div>
                        <span
                          className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}
                        >
                          {["Processing", "Analyzing", "Optimizing"][index]}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            ))}
        </AnimatePresence>

        {/* Main content */}
        <div className="w-full max-w-7xl mx-auto flex-1 relative z-10">
          {/* Step indicator */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-8"
          >
            <StepIndicator
              step={1}
              currentStep={currentStep}
              label="Report Type"
              theme={theme}
            />
            <div
              className={cn(
                "flex-1 max-w-[60px] h-0.5 rounded-full transition-all duration-500",
                currentStep > 1
                  ? "bg-gradient-to-r from-blue-500 to-purple-600"
                  : theme === "dark"
                    ? "bg-gray-700"
                    : "bg-gray-200 dark:bg-slate-800",
              )}
            />
            <StepIndicator
              step={2}
              currentStep={currentStep}
              label="Configure & Upload"
              theme={theme}
            />
          </motion.div>

          {/* Step 1: Report Type + Sub Category */}
          <motion.div
            layout
            className={cn(
              "rounded-3xl border-2 shadow-2xl backdrop-blur-sm overflow-hidden mb-8",
              theme === "dark"
                ? "bg-gray-800/70 border-gray-700"
                : "bg-white/90 dark:bg-slate-900/90 border-gray-200 dark:border-slate-700",
            )}
          >
            <div
              className={cn(
                "flex items-center gap-4 px-8 py-5 border-b",
                theme === "dark" ? "border-gray-700" : "border-gray-100 dark:border-slate-800",
              )}
            >
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3
                  className={cn(
                    "text-xl font-bold",
                    theme === "dark" ? "text-white" : "text-gray-900 dark:text-white",
                  )}
                >
                  Report Classification
                </h3>
                <p
                  className={cn(
                    "text-sm",
                    theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400",
                  )}
                >
                  Select the type and sub-category to get started
                </p>
              </div>
              <AnimatePresence>
                {sector && subSector && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, x: 10 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-600/20 border border-blue-500/30"
                  >
                    <CheckCircle2 className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                    <span
                      className={cn(
                        "text-xs font-semibold",
                        theme === "dark" ? "text-blue-300" : "text-blue-600 dark:text-blue-400",
                      )}
                    >
                      {sectors.find((s) => s.id === parseInt(sector))?.name} ·{" "}
                      {
                        subSectors.find((s) => s.id === parseInt(subSector))
                          ?.name
                      }
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-8 py-6">
              <div className="space-y-2">
                <Label
                  className={cn(
                    "text-sm font-semibold",
                    theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                  )}
                >
                  Report Type <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <motion.select
                  whileFocus={{ scale: 1.01 }}
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                  disabled={isLoadingSectors}
                  className={selectCls}
                >
                  <option value="">
                    {isLoadingSectors
                      ? "Loading sectors..."
                      : sectorsError
                        ? "Error loading sectors"
                        : "Select Report Type"}
                  </option>
                  {sectors.map((sector) => (
                    <option key={sector.id} value={sector.id}>
                      {sector.name}
                    </option>
                  ))}
                </motion.select>
                {sectorsError && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{sectorsError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  className={cn(
                    "text-sm font-semibold",
                    theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                  )}
                >
                  Sub Category <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <motion.select
                  whileFocus={{ scale: 1.01 }}
                  value={subSector}
                  onChange={(e) => setSubSector(e.target.value)}
                  disabled={!sector || isLoadingSubSectors}
                  className={selectCls}
                >
                  <option value="">
                    {!sector
                      ? "Select a report type first"
                      : isLoadingSubSectors
                        ? "Loading sub-categories..."
                        : subSectorsError
                          ? "Error loading sub-categories"
                          : "Select Sub Category"}
                  </option>
                  {subSectors.map((subSector) => (
                    <option key={subSector.id} value={subSector.id}>
                      {subSector.name}
                    </option>
                  ))}
                </motion.select>
                {subSectorsError && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{subSectorsError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  className={cn(
                    "text-sm font-semibold",
                    theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                  )}
                >
                  Convert Output Values In{" "}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <motion.select
                  whileFocus={{ scale: 1.01 }}
                  value={convertResIn}
                  onChange={(e) => setConvertResIn(e.target.value)}
                  className={selectCls}
                >
                  <option value="thousand">Thousand</option>
                  <option value="million">Million</option>
                  <option value="billion">Billion</option>
                </motion.select>
              </div>
            </div>

            <AnimatePresence>
              {!sector && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div
                    className={cn(
                      "flex items-center justify-center gap-2 py-4 px-8 border-t text-sm font-medium",
                      theme === "dark"
                        ? "border-gray-700/50 text-gray-500"
                        : "border-gray-100 dark:border-slate-800 text-gray-400",
                    )}
                  >
                    <ChevronRight className="w-4 h-4" />
                    Select a report type above to continue
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Step 2: Conditional rendering based on sector */}
          <AnimatePresence>
            {sector && subSector && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="space-y-8"
              >
                {sectors.find((s) => s.id === parseInt(sector))?.name ===
                "NBFI" ? (
                  // For NBFI: show single mode directly (no toggle)
                  <div className="space-y-8">
                    {renderConfigurationAndRequirements()}
                    <motion.div
                      ref={uploadSectionRef}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                      className="w-full"
                    >
                      {renderReportInput()}
                    </motion.div>
                  </div>
                ) : (
                  // For FI: show mode toggle and conditional single/bulk
                  <>
                    {/* Mode toggle header */}
                    <div
                      className={cn(
                        "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-8 py-5 rounded-3xl border-2 shadow-lg",
                        theme === "dark"
                          ? "bg-gray-800/60 border-gray-700"
                          : "bg-white/80 dark:bg-slate-900/80 border-gray-200 dark:border-slate-700",
                      )}
                    >
                      <div>
                        <h3
                          className={cn(
                            "text-lg font-bold",
                            theme === "dark" ? "text-white" : "text-gray-900 dark:text-white",
                          )}
                        >
                          Upload Mode
                        </h3>
                        <p
                          className={cn(
                            "text-sm mt-0.5",
                            theme === "dark"
                              ? "text-gray-400"
                              : "text-gray-500 dark:text-slate-400",
                          )}
                        >
                          {isBulkUpload
                            ? "Upload multiple files with independent configurations"
                            : "Upload one file with global configuration"}
                        </p>
                      </div>
                      <UploadModeTab
                        isBulkUpload={isBulkUpload}
                        setIsBulkUpload={setIsBulkUpload}
                        theme={theme}
                      />
                    </div>

                    <AnimatePresence mode="wait">
                      {!isBulkUpload ? (
                        <motion.div
                          key="single"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                          className="space-y-8"
                        >
                          {renderConfigurationAndRequirements()}
                          <motion.div
                            ref={uploadSectionRef}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                            className="w-full"
                          >
                            {renderReportInput()}
                          </motion.div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="bulk"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.3 }}
                          className="w-full"
                        >
                          <BulkFileUpload
                            sector={sectors.find(
                              (s) => s.id === parseInt(sector),
                            )}
                            subSector={subSectors.find(
                              (s) => s.id === parseInt(subSector),
                            )}
                            countries={countries}
                            currencies={currencies}
                            onStartBulkAnalysis={handleBulkSubmit}
                            isProcessing={isProcessing}
                            error={error}
                            accessToken={user?.access_token || ""}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </DashboardLayout>
  );
}
